
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  FormHelperText,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Pagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Button from "../components/common/styled components/Button";
import CommonGrid from "../components/common/Grid";
import api from "../axios/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../components/common/Loader";

//  Validation Schema
const validationSchema = Yup.object().shape({
  companyName: Yup.string()
    .required("Company Name is required *")
    .min(2, "Must be at least 2 characters")
    .max(100, "Cannot exceed 100 characters"),
  industryType: Yup.string().required("Industry Type is required *"),
  verifiedTree: Yup.number()
    .required("Verified Tree is required *")
    .positive("Must be a positive number")
    .min(0, "Cannot be negative")
    .integer("Must be a whole number")
    .typeError("Must be a number"),
  totalTree: Yup.number()
    .required("Total Tree is required *")
    .positive("Must be a positive number")
    .min(0, "Cannot be negative")
    .integer("Must be a whole number")
    .typeError("Must be a number"),
  deletedTree: Yup.number()
    .required("Deleted Tree is required *")
    .min(0, "Cannot be negative")
    .integer("Must be a whole number")
    .typeError("Must be a number"),
  geoLocatedCount: Yup.number()
    .required("Geo-located Count is required *")
    .min(0, "Cannot be negative")
    .integer("Must be a whole number")
    .typeError("Must be a number"),
  ordering: Yup.number()
    .required("Ordering is required *")
    .min(1, "Must be at least 1")
    .integer("Must be a whole number")
    .typeError("Must be a number"),
  image1: Yup.mixed().required("First image is required *"),
  image2: Yup.mixed().required("Second image is required *"),
});

//  Initial Values
const initialValues = {
  id: "",
  companyName: "",
  industryType: "",
  verifiedTree: "",
  totalTree: "",
  deletedTree: "",
  geoLocatedCount: "",
  activeStatus: true,
  ordering: "",
  image1: null,
  image2: null,
};

const Companies = () => {
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [industry_types, setIndustryTypes] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [page, setPage] = useState(1);
  const itemsPerPage = 5; // Number of items to show per page

  //  For delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
    setSelectedCompany(null);
  };

  useEffect(() => {
    const fetchIndustryTypes = async () => {
      try {
        const response = await api.get("/api/org/getIndustryTypes");
        setIndustryTypes(response.data);
      } catch (error) {
        toast.error("Error fetching industry types");
        console.error("Error fetching industry types:", error);
      }
    };
    fetchIndustryTypes();
  }, []);


  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return toast.error(" No token found");

        const response = await api.get("/api/org/getcompanydata", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const info =
          response.data.CompanyInfo ||
          response.data.data ||
          response.data.companies ||
          [];

        setCompanyData(Array.isArray(info) ? info : [info]);

      } catch (error) {

        console.error(" Error fetching company data:", error);
      }
    };
    fetchCompanyData();
  }, []);

  //  Edit Button → Prefill form
  const handleEdit = (company) => {
    setEditMode(true);
    setSelectedCompany({
      id: company.id,
      companyName: company.company_name || "",
      industryType: company.industry_type_id || "",
      verifiedTree: company.verifiedTrees || "",
      totalTree: company.totalTrees || "",
      deletedTree: company.deletedTrees || "",
      geoLocatedCount: company.geoLocatedTreesCount || "",
      activeStatus: company.is_active ?? true,
      ordering: company.ordering || "",
      image1: company.company_logo || "",
      image2: company.company_cover_pic || "",
    });
    setOpen(true);
     

  };

  //  Open Delete Confirmation Dialog
  const openDeleteDialog = (company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setCompanyToDelete(null);
    setDeleteDialogOpen(false);
  };

  //  Delete Functionality
  const confirmDelete = async () => {
    if (!companyToDelete) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Unauthorized - No token found");
        return;
      }

      const response = await api.delete(
        `api/org/deletedetails/${companyToDelete.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Company deleted successfully!");
      setCompanyData((prevData) =>
        prevData.filter((item) => item.id !== companyToDelete.id)
      );
      closeDeleteDialog();
    } catch (error) {
      console.error("❌ Error deleting company:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong while deleting.";
      toast.error(errorMessage);
      closeDeleteDialog();
    }
  };

  // Submit Function
  const handleSubmit = async (values, { resetForm }) => {
    try {
      const formData = new FormData();
      formData.append("companyName", values.companyName);
      formData.append("industryType", values.industryType);
      formData.append("verifiedTree", values.verifiedTree);
      formData.append("totalTree", values.totalTree);
      formData.append("deletedTree", values.deletedTree);
      formData.append("geoLocatedCount", values.geoLocatedCount);
      formData.append("activeStatus", values.activeStatus);
      formData.append("ordering", values.ordering);

      if (values.image1 instanceof File) formData.append("image1", values.image1);
      if (values.image2 instanceof File) formData.append("image2", values.image2);

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("User not logged in. Please login again.");
        return;
      }

      let response;

      //  Update mode
      if (values.id) {
        response = await api.put(`/api/org/updatedetails/${values.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const updatedCompany = response.data?.company;
        if (updatedCompany) {
          setCompanyData((prevData) =>
            prevData.map((item) =>
              item.id === updatedCompany.id ? { ...item, ...updatedCompany } : item
            )
          );
        }

        toast.success("Company updated successfully!");
      }
      //  Create mode
      else {
        response = await api.post("/api/org/createcompany", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const newCompany = response.data?.CompanyInfo;
        if (newCompany) {
          setCompanyData((prevData) => [
            ...prevData,
            { ...newCompany, industry_type_id: values.industryType },
          ]);
        }

        toast.success("Company added successfully!");
      }

      resetForm();
      handleClose();
    } catch (error) {
      console.error("❌ Error submitting form:", error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong while submitting the form.";
      toast.error(msg);
    }
  };



  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleActiveStatusChange = async (companyId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Unauthorized - No token found");
        return;
      }


      const response = await api.put(
        `api/org/updatestatus/${companyId}`,
        { is_active: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );


      setCompanyData((prev) =>
        prev.map((item) =>
          item.id === companyId ? { ...item, is_active: newStatus } : item
        )
      );

      toast.success(`Company marked as ${newStatus ? "Active" : "Inactive"} successfully!`);
    } catch (error) {
      console.error("❌ Error updating active status:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong while updating status.";
      toast.error(errorMessage);
    }
  };


  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 4,
                height: 28,
                bgcolor: "#10b981",
                borderRadius: "4px",
                flexShrink: 0,
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontFamily: "'Plus Jakarta Sans', 'Outfit', 'Inter', sans-serif",
                fontSize: { xs: "1.4rem", sm: "1.75rem" },
                fontWeight: 700,
                color: "#002F43",
                letterSpacing: "-0.02em",
                m: 0,
                lineHeight: 1.2,
              }}
            >
              Company Master
            </Typography>
          </Box>
          <Button
            onClick={handleOpen}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",

              padding: "10px 10px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >

            <AddIcon />
            Add New Companies
          </Button>
        </Box>

        {/*  Dialog Form */}
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {editMode ? "Edit Company Details" : "Add Company Details"}
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Formik
            initialValues={selectedCompany || initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form>
                <DialogContent>
                  <Grid container spacing={2}>
                    {/*  Row 1 — 3 text fields */}
                    <Grid item xs={12} sm={4}>
                      <Field name="companyName">
                        {({ field, form: { setFieldValue } }) => (
                          <TextField
                            {...field}
                            label="Company Name"
                            variant="outlined"
                            fullWidth
                            InputProps={{ style: { height: "56px" } }}
                            onKeyDown={(e) => {
                              if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (!value.startsWith("-")) setFieldValue("companyName", value);
                            }}
                            error={touched.companyName && Boolean(errors.companyName)}
                            helperText={touched.companyName && errors.companyName}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        error={touched.industryType && Boolean(errors.industryType)}
                      >
                        <InputLabel>Industry Type</InputLabel>
                        <Field
                          name="industryType"
                          as={Select}
                          label="Industry Type"
                          value={values.industryType}
                          onChange={(e) => setFieldValue("industryType", e.target.value)}
                        >
                          {industry_types.length > 0 ? (
                            industry_types.map((item) => (
                              <MenuItem key={item.id} value={item.id}>
                                {item.type_name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled><Loader/></MenuItem>
                          )}
                        </Field>
                        <FormHelperText>
                          {touched.industryType && errors.industryType}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Field name="verifiedTree">
                        {({ field: f }) => (
                          <TextField
                            {...f}
                            label="Verified Tree"
                            type="number"
                            fullWidth
                            error={touched.verifiedTree && Boolean(errors.verifiedTree)}
                            helperText={touched.verifiedTree && errors.verifiedTree}
                            inputProps={{
                              min: 0,
                              onKeyDown: (e) => {
                                if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                              },
                            }}
                          />
                        )}
                      </Field>
                    </Grid>

                    {/*  Row 2 — 3 text fields */}
                    {[
                      { name: "totalTree", label: "Total Tree" },
                      { name: "deletedTree", label: "Deleted Tree" },
                      { name: "geoLocatedCount", label: "Geo-located Count" },
                    ].map((field) => (
                      <Grid item xs={12} sm={4} key={field.name}>
                        <Field name={field.name}>
                          {({ field: f }) => (
                            <TextField
                              {...f}
                              label={field.label}
                              type="number"
                              fullWidth
                              error={touched[field.name] && Boolean(errors[field.name])}
                              helperText={touched[field.name] && errors[field.name]}
                              inputProps={{
                                min: 0,
                                onKeyDown: (e) => {
                                  if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                                },
                              }}
                            />
                          )}
                        </Field>
                      </Grid>
                    ))}

                    {/*  Row 3 — 1 text field + 2 image uploads */}
                    <Grid item xs={12} sm={4}>
                      <Field name="ordering">
                        {({ field: f }) => (
                          <TextField
                            {...f}
                            label="Ordering"
                            type="number"
                            fullWidth
                            error={touched.ordering && Boolean(errors.ordering)}
                            helperText={touched.ordering && errors.ordering}
                            inputProps={{
                              min: 0,
                              onKeyDown: (e) => {
                                if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                              },
                            }}
                          />
                        )}
                      </Field>
                    </Grid>

                    {[{ name: "image1", label: "Upload Logo Image" },
                    { name: "image2", label: "Upload Cover Image" }].map((imgField) => (
                      <Grid item xs={12} sm={4} key={imgField.name}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography>{imgField.label}</Typography>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.currentTarget.files[0];
                              if (file) setFieldValue(imgField.name, file);
                            }}
                          />
                          {touched[imgField.name] && errors[imgField.name] && (
                            <FormHelperText error>{errors[imgField.name]}</FormHelperText>
                          )}
                          {values[imgField.name] && (
                            <img
                              src={
                                values[imgField.name] instanceof File
                                  ? URL.createObjectURL(values[imgField.name])
                                  : values[imgField.name]
                              }
                              alt="preview"
                              style={{
                                width: 100,
                                height: 100,
                                borderRadius: "8px",
                                border: "1px solid #ccc",
                                objectFit: "cover",
                                marginTop: "4px",
                              }}
                            />
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </DialogContent>



                <DialogActions>
                  <Button
                    type="button"
                    onClick={handleClose}
                    variant="cancel"
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : editMode ? "Update" : "Submit"}
                  </Button>
                </DialogActions>

              </Form>
            )}
          </Formik>
        </Dialog>

        {/*  Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete{" "}
              <strong>{companyToDelete?.company_name}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} variant="cancel">
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Company Table using CommonGrid */}
        <CommonGrid
          columns={[
            {
              header: "Company Name",
              accessor: "company_name",
              sortable: true,
              align: "left",
            },
            {
              header: "Industry Type",
              accessor: "industry_type_name",
              sortable: true,
              align: "left",
            },
            {
              header: "Verified Trees",
              accessor: "verifiedTrees",
              sortable: true,
              align: "center",
              render: (val) => val ?? 0,
            },
            {
              header: "Total Trees",
              accessor: "totalTrees",
              sortable: true,
              align: "center",
              render: (val) => val ?? 0,
            },
            {
              header: "Geo Located Trees",
              accessor: "geoLocatedTreesCount",
              sortable: true,
              align: "center",
              render: (val) => val ?? 0,
            },
            {
              header: "Deleted Trees",
              accessor: "deletedTrees",
              sortable: true,
              align: "center",
              render: (val) => val ?? 0,
            },
            {
              header: "Ordering",
              accessor: "ordering",
              sortable: true,
              align: "center",
              render: (val) => val ?? "-",
            },
            {
              header: "Logo",
              align: "center",
              render: (_, CompanyInfo) =>
                CompanyInfo.company_logo ? (
                  <img
                    src={CompanyInfo.company_logo}
                    alt="Logo"
                    width="40"
                    height="40"
                    style={{
                      borderRadius: "6px",
                      objectFit: "cover",
                      display: "inline-block",
                    }}
                  />
                ) : (
                  "N/A"
                ),
            },
            {
              header: "Cover",
              align: "center",
              render: (_, CompanyInfo) =>
                CompanyInfo.company_cover_pic ? (
                  <img
                    src={CompanyInfo.company_cover_pic}
                    alt="Cover"
                    width="40"
                    height="40"
                    style={{
                      borderRadius: "6px",
                      objectFit: "cover",
                      display: "inline-block",
                    }}
                  />
                ) : (
                  "N/A"
                ),
            },
            {
              header: "Status",
              align: "center",
              render: (_, CompanyInfo) => (
                <Switch
                  checked={Boolean(CompanyInfo.is_active)}
                  onChange={(e) =>
                    handleActiveStatusChange(CompanyInfo.id, e.target.checked)
                  }
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "green",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      color: "green",
                      backgroundColor: "green",
                      opacity: 1,
                    },
                    "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track": {
                      color: "red",
                      backgroundColor: "red",
                      opacity: 1,
                    },
                  }}
                />
              ),
            },
            {
              header: "Actions",
              align: "center",
              render: (_, CompanyInfo) => (
                <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                  <IconButton
                    color="primary"
                    size="small"
                    title="Edit"
                    onClick={() => handleEdit(CompanyInfo)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    color="error"
                    size="small"
                    title="Delete"
                    onClick={() => openDeleteDialog(CompanyInfo)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ),
            },
          ]}
          data={companyData}
          variant="navy"
          pagination={true}
          pageSize={itemsPerPage}
          pageSizeOptions={[5, 10, 20, 50]}
          rowKey="id"
        />
      </Box>
    </>
  );
};

export default Companies;
